import React from 'react';
import { Card, CardContent, Typography, Box, Avatar, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { format } from 'date-fns';
import { Transaction } from '../../types';
import { ArrowUpward, ArrowDownward, SwapHoriz, AccessTime } from '@mui/icons-material';

const TransactionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  '&:hover': {
    backgroundColor: theme.palette.grey[50],
  },
}));

const TransactionIcon = styled(Avatar)<{ transactionType: string }>(({ theme, transactionType }) => {
  const getColor = () => {
    switch (transactionType) {
      case 'earned': return theme.palette.success.main;
      case 'redeemed': return theme.palette.primary.main;
      case 'transferred': return theme.palette.secondary.main;
      case 'expired': return theme.palette.error.main;
      default: return theme.palette.grey[500];
    }
  };

  return {
    backgroundColor: getColor(),
    width: 40,
    height: 40,
  };
});

interface TransactionItemProps {
  transaction: Transaction;
  programName?: string;
}

export const TransactionItem: React.FC<TransactionItemProps> = ({ 
  transaction, 
  programName 
}) => {
  const getIcon = () => {
    switch (transaction.type) {
      case 'earned': return <ArrowUpward />;
      case 'redeemed': return <ArrowDownward />;
      case 'transferred': return <SwapHoriz />;
      case 'expired': return <AccessTime />;
      default: return <SwapHoriz />;
    }
  };

  const formatPoints = (points: number): string => {
    if (isNaN(points) || points === null || points === undefined) points = 0;
    const sign = transaction.type === 'earned' ? '+' : '-';
    return `${sign}${points.toLocaleString()}`;
  };

  const getPointsColor = (): string => {
    switch (transaction.type) {
      case 'earned': return '#48BB78';
      case 'redeemed': return '#4299E1';
      case 'transferred': return '#38B2AC';
      case 'expired': return '#F56565';
      default: return '#718096';
    }
  };

  return (
    <TransactionCard variant="outlined">
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TransactionIcon transactionType={transaction.type}>
            {getIcon()}
          </TransactionIcon>

          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
              {transaction.description}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              {programName && (
                <Chip 
                  label={programName} 
                  size="small" 
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: 20 }}
                />
              )}
              <Typography variant="caption" color="textSecondary">
                {format(new Date(transaction.date), 'MMM dd, yyyy')}
              </Typography>
            </Box>

            {transaction.source && (
              <Typography variant="caption" color="textSecondary">
                Source: {transaction.source}
              </Typography>
            )}
          </Box>

          <Box sx={{ textAlign: 'right' }}>
            <Typography 
              variant="body1" 
              sx={{ 
                fontWeight: 700, 
                color: getPointsColor(),
                mb: 0.5 
              }}
            >
              {formatPoints(transaction.points)}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Balance: {(transaction.balance || 0).toLocaleString()}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </TransactionCard>
  );
};